import { useMemo, useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import configuracionContratosService from '../services/configuracionContratosService';
import configuracionSsomaService from '../services/configuracionSsomaService';

/** Estado compartido: Sidebar y ProtectedRoute montan useNavigation por separado. */
const contratosConfigStore = {
  empresaId: null,
  activo: false,
  cargando: false,
  inicializado: false,
};

const contratosConfigSubscribers = new Set();
let contratosConfigPromise = null;
let contratosConfigPromiseEmpresaId = null;

function normalizarEmpresaId(id) {
  if (id == null || id === '') return null;
  return String(id);
}

function mismaEmpresaId(a, b) {
  return normalizarEmpresaId(a) === normalizarEmpresaId(b);
}

function claveContratosSesion(empresaId) {
  return `contratos_modulo_activo_${normalizarEmpresaId(empresaId)}`;
}

function guardarContratosActivoEnSesion(empresaId, activo) {
  const norm = normalizarEmpresaId(empresaId);
  if (!norm) return;
  try {
    sessionStorage.setItem(claveContratosSesion(norm), activo ? '1' : '0');
  } catch {
    /* ignore */
  }
}

function leerContratosActivoEnSesion(empresaId) {
  const norm = normalizarEmpresaId(empresaId);
  if (!norm) return false;
  try {
    return sessionStorage.getItem(claveContratosSesion(norm)) === '1';
  } catch {
    return false;
  }
}

const ssomaConfigStore = {
  empresaId: null,
  activo: false,
  cargando: false,
  inicializado: false,
};

const ssomaConfigSubscribers = new Set();
let ssomaConfigPromise = null;
let ssomaConfigPromiseEmpresaId = null;

function claveSsomaSesion(empresaId) {
  return `ssoma_modulo_activo_${normalizarEmpresaId(empresaId)}`;
}

function guardarSsomaActivoEnSesion(empresaId, activo) {
  const norm = normalizarEmpresaId(empresaId);
  if (!norm) return;
  try {
    sessionStorage.setItem(claveSsomaSesion(norm), activo ? '1' : '0');
  } catch {
    /* ignore */
  }
}

function leerSsomaActivoEnSesion(empresaId) {
  const norm = normalizarEmpresaId(empresaId);
  if (!norm) return false;
  try {
    return sessionStorage.getItem(claveSsomaSesion(norm)) === '1';
  } catch {
    return false;
  }
}

function estaModuloSsomaActivo(empresaId) {
  const norm = normalizarEmpresaId(empresaId);
  if (!norm) return false;
  if (mismaEmpresaId(ssomaConfigStore.empresaId, norm) && ssomaConfigStore.activo) {
    return true;
  }
  return leerSsomaActivoEnSesion(norm);
}

function notificarSsomaConfig() {
  ssomaConfigSubscribers.forEach((cb) => cb());
}

function actualizarSsomaConfigStore(empresaId, patch) {
  if (empresaId !== undefined) {
    ssomaConfigStore.empresaId = normalizarEmpresaId(empresaId);
  }
  if (patch.activo !== undefined) {
    ssomaConfigStore.activo = patch.activo;
    if (ssomaConfigStore.empresaId != null) {
      guardarSsomaActivoEnSesion(ssomaConfigStore.empresaId, patch.activo);
    }
  }
  if (patch.cargando !== undefined) {
    ssomaConfigStore.cargando = patch.cargando;
  }
  if (patch.inicializado !== undefined) {
    ssomaConfigStore.inicializado = patch.inicializado;
  }
  notificarSsomaConfig();
}

function estaModuloContratosActivo(empresaId) {
  const norm = normalizarEmpresaId(empresaId);
  if (!norm) return false;
  if (mismaEmpresaId(contratosConfigStore.empresaId, norm) && contratosConfigStore.activo) {
    return true;
  }
  return leerContratosActivoEnSesion(norm);
}

function notificarContratosConfig() {
  contratosConfigSubscribers.forEach((cb) => cb());
}

function actualizarContratosConfigStore(empresaId, patch) {
  if (empresaId !== undefined) {
    contratosConfigStore.empresaId = normalizarEmpresaId(empresaId);
  }
  if (patch.activo !== undefined) {
    contratosConfigStore.activo = patch.activo;
    if (contratosConfigStore.empresaId != null) {
      guardarContratosActivoEnSesion(contratosConfigStore.empresaId, patch.activo);
    }
  }
  if (patch.cargando !== undefined) {
    contratosConfigStore.cargando = patch.cargando;
  }
  if (patch.inicializado !== undefined) {
    contratosConfigStore.inicializado = patch.inicializado;
  }
  notificarContratosConfig();
}

async function cargarContratosConfigCompartida(empresaId, { forzar = false } = {}) {
  const empresaNorm = normalizarEmpresaId(empresaId);

  if (!empresaNorm) {
    actualizarContratosConfigStore(null, {
      activo: false,
      cargando: false,
      inicializado: true,
    });
    return;
  }

  const mismaEmpresa = mismaEmpresaId(contratosConfigStore.empresaId, empresaNorm);

  if (!forzar && mismaEmpresa && contratosConfigStore.inicializado && !contratosConfigStore.cargando) {
    return;
  }

  if (
    !forzar &&
    contratosConfigPromise &&
    mismaEmpresaId(contratosConfigPromiseEmpresaId, empresaNorm)
  ) {
    return contratosConfigPromise;
  }

  if (!mismaEmpresa) {
    const activoSesion = leerContratosActivoEnSesion(empresaNorm);
    actualizarContratosConfigStore(empresaNorm, {
      activo: activoSesion,
      cargando: true,
      inicializado: false,
    });
  } else {
    actualizarContratosConfigStore(empresaNorm, { cargando: true });
  }

  contratosConfigPromiseEmpresaId = empresaNorm;
  contratosConfigPromise = (async () => {
    try {
      const response = await configuracionContratosService.obtenerConfiguracion(empresaNorm);
      actualizarContratosConfigStore(empresaNorm, {
        activo: response.success ? (response.data?.activo ?? false) : false,
        cargando: false,
        inicializado: true,
      });
    } catch {
      actualizarContratosConfigStore(empresaNorm, {
        activo: false,
        cargando: false,
        inicializado: true,
      });
    } finally {
      contratosConfigPromise = null;
      contratosConfigPromiseEmpresaId = null;
    }
  })();

  return contratosConfigPromise;
}

/** Consultar al iniciar sesión / al elegir empresa si el módulo de contratos está activo. */
export function precargarConfiguracionContratos(empresaId) {
  return cargarContratosConfigCompartida(empresaId);
}

async function cargarSsomaConfigCompartida(empresaId, { forzar = false } = {}) {
  const empresaNorm = normalizarEmpresaId(empresaId);

  if (!empresaNorm) {
    actualizarSsomaConfigStore(null, {
      activo: false,
      cargando: false,
      inicializado: true,
    });
    return;
  }

  const mismaEmpresa = mismaEmpresaId(ssomaConfigStore.empresaId, empresaNorm);

  if (!forzar && mismaEmpresa && ssomaConfigStore.inicializado && !ssomaConfigStore.cargando) {
    return;
  }

  if (
    !forzar &&
    ssomaConfigPromise &&
    mismaEmpresaId(ssomaConfigPromiseEmpresaId, empresaNorm)
  ) {
    return ssomaConfigPromise;
  }

  if (!mismaEmpresa) {
    const activoSesion = leerSsomaActivoEnSesion(empresaNorm);
    actualizarSsomaConfigStore(empresaNorm, {
      activo: activoSesion,
      cargando: true,
      inicializado: false,
    });
  } else {
    actualizarSsomaConfigStore(empresaNorm, { cargando: true });
  }

  ssomaConfigPromiseEmpresaId = empresaNorm;
  ssomaConfigPromise = (async () => {
    try {
      const response = await configuracionSsomaService.obtenerConfiguracion(empresaNorm);
      actualizarSsomaConfigStore(empresaNorm, {
        activo: response.success ? (response.data?.activo ?? false) : false,
        cargando: false,
        inicializado: true,
      });
    } catch {
      actualizarSsomaConfigStore(empresaNorm, {
        activo: false,
        cargando: false,
        inicializado: true,
      });
    } finally {
      ssomaConfigPromise = null;
      ssomaConfigPromiseEmpresaId = null;
    }
  })();

  return ssomaConfigPromise;
}

export function precargarConfiguracionSsoma(empresaId) {
  return cargarSsomaConfigCompartida(empresaId);
}

/**
 * Hook personalizado para manejar la navegación basada en roles
 */
export const useNavigation = () => {
  const { user, selectedCompany } = useAuth();
  const [, setRevision] = useState(0);

  useEffect(() => {
    const refresh = () => setRevision((n) => n + 1);
    contratosConfigSubscribers.add(refresh);
    ssomaConfigSubscribers.add(refresh);
    return () => {
      contratosConfigSubscribers.delete(refresh);
      ssomaConfigSubscribers.delete(refresh);
    };
  }, []);

  useEffect(() => {
    const empresaId = selectedCompany?.id;

    cargarContratosConfigCompartida(empresaId);
    cargarSsomaConfigCompartida(empresaId);

    const handleConfiguracionContratosActualizada = () => {
      cargarContratosConfigCompartida(empresaId, { forzar: true });
    };
    const handleConfiguracionSsomaActualizada = () => {
      cargarSsomaConfigCompartida(empresaId, { forzar: true });
    };

    window.addEventListener('configuracion-contratos-actualizada', handleConfiguracionContratosActualizada);
    window.addEventListener('configuracion-ssoma-actualizada', handleConfiguracionSsomaActualizada);

    return () => {
      window.removeEventListener('configuracion-contratos-actualizada', handleConfiguracionContratosActualizada);
      window.removeEventListener('configuracion-ssoma-actualizada', handleConfiguracionSsomaActualizada);
    };
  }, [selectedCompany?.id]);

  const empresaIdActual = normalizarEmpresaId(selectedCompany?.id);

  const contratosActivados = estaModuloContratosActivo(empresaIdActual);
  const ssomaActivado = estaModuloSsomaActivo(empresaIdActual);

  const mostrarPlantillasEnMenu = contratosActivados;
  const mostrarSsomaEnMenu = ssomaActivado;

  const contratosConfigCargando =
    !!empresaIdActual &&
    mismaEmpresaId(contratosConfigStore.empresaId, empresaIdActual) &&
    (!contratosConfigStore.inicializado || contratosConfigStore.cargando);

  // Función para verificar si el usuario es Supervisor
  const isSupervisor = useMemo(() => {
    if (!user) return false;

    const isSupervisorByName = user.name && user.name.toLowerCase().includes('supervisor');

    if (!user.roles) {
      return isSupervisorByName;
    }

    const isSupervisorByRole = user.roles.some((role) => {
      if (typeof role === 'string') {
        return role === 'Supervisor';
      }
      if (typeof role === 'object' && role.name) {
        return role.name === 'Supervisor';
      }
      return false;
    });

    return isSupervisorByRole || isSupervisorByName;
  }, [user]);

  const isSsoma = useMemo(() => {
    if (!user) return false;

    const byName = user.name && user.name.toLowerCase().includes('ssoma');

    if (!user.roles) {
      return byName;
    }

    const byRole = user.roles.some((role) => {
      const nombre = typeof role === 'string' ? role : role?.name;
      return String(nombre || '').toLowerCase() === 'ssoma';
    });

    return byRole || byName;
  }, [user]);

  const canAccessRoute = useMemo(() => {
    return (path) => {
      if (isSupervisor) {
        const allowedPaths = ['/tareos', '/autorizaciones'];
        return allowedPaths.includes(path);
      }

      if (isSsoma) {
        return path === '/' || path === '/ssoma' || path.startsWith('/ssoma/');
      }

      return true;
    };
  }, [isSupervisor, isSsoma]);

  const filteredNavItems = useMemo(() => {
    const allNavItems = [
      {
        path: '/',
        label: 'Panel principal',
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v14l-5-3-5 3V5z" />
          </svg>
        ),
      },
      {
        path: '/empleados',
        label: 'Empleados',
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>
        ),
      },
      ...(mostrarPlantillasEnMenu
        ? [
            {
              path: '/plantillas',
              label: 'Plantillas',
              icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9l-7-7z"
                  />
                </svg>
              ),
            },
          ]
        : []),
      {
        path: '/tareos',
        label: 'Tareos',
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
      },
      ...(mostrarSsomaEnMenu
        ? [
            {
              path: '/ssoma',
              label: 'SSOMA',
              icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              ),
            },
          ]
        : []),
      {
        path: '/autorizaciones',
        label: 'Autorizaciones',
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
      },
      {
        path: '/planillas',
        label: 'Planillas',
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        ),
      },
      {
        path: selectedCompany?.id ? `/calculos/${selectedCompany.id}` : '/calculos',
        label: 'Cálculos',
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        path: '/reportes',
        label: 'Reportes',
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        ),
      },
      {
        path: '/configuracion',
        label: 'Configuración',
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        ),
      },
    ];

    if (isSupervisor || isSsoma) {
      return allNavItems.filter((item) => canAccessRoute(item.path));
    }

    return allNavItems;
  }, [isSupervisor, isSsoma, canAccessRoute, selectedCompany?.id, mostrarPlantillasEnMenu, mostrarSsomaEnMenu]);

  const getDefaultRoute = useMemo(() => {
    if (isSupervisor) {
      return '/tareos';
    }
    if (isSsoma) {
      return '/';
    }
    return '/';
  }, [isSupervisor, isSsoma]);

  return {
    isSupervisor,
    isSsoma,
    canAccessRoute,
    filteredNavItems,
    getDefaultRoute,
    contratosActivados,
    mostrarPlantillasEnMenu,
    contratosConfigCargando,
    ssomaActivado,
    mostrarSsomaEnMenu,
    user,
  };
};
